import { useEffect, useRef, type ReactNode } from "react";
import { colors } from "../design-system";
import { useIsTabletUp } from "../hooks/useMediaQuery";
import { AppPressable } from "./AppPressable";
import { AppText } from "./AppText";
import { Icon } from "./Icon";

type AppModalProps = {
  visible: boolean;
  onClose: () => void;
  /** Titre de la feuille — aligné à gauche, façon checkout sheet. */
  title?: string;
  /** Ligne d'explication sous le titre. */
  subtitle?: string;
  /** Masque la croix (étape bloquante : on force un choix explicite). */
  hideClose?: boolean;
  /** Empêche la fermeture au clic sur le fond (décision irréversible). */
  dismissOnBackdrop?: boolean;
  /** Hauteur max de la feuille : "half" (60%), "tall" (85%), "auto" (contenu). */
  size?: "auto" | "half" | "tall";
  children: ReactNode;
};

const MAX_HEIGHT: Record<NonNullable<AppModalProps["size"]>, string> = {
  auto: "max-h-[85%]",
  half: "max-h-[60%]",
  tall: "max-h-[85%]",
};

/**
 * Feuille modale de l'app — un seul langage pour tous les modaux.
 *
 * ## Anatomie
 *   ┌─────────────────────────┐
 *   │        ────  (poignée)  │  affordance de glissement
 *   │  Titre            [✕]   │  titre à gauche, fermeture évidente
 *   │  Sous-titre             │
 *   ├─────────────────────────┤  filet : ancre le header
 *   │  contenu…               │
 *   └─────────────────────────┘
 *
 * ## Ce que le web change
 *
 * **`<dialog>` remplace `<Modal>`.** L'élément natif apporte gratuitement ce que
 * React Native n'avait pas et qu'il aurait fallu écrire à la main :
 * piégeage du focus dans la modale, fermeture par Échap, inertisation du fond
 * pour les lecteurs d'écran, et empilement au-dessus de tout sans bricoler de
 * z-index. `showModal()` / `close()` pilotent l'ouverture.
 *
 * **`statusBarTranslucent` et les safe-areas** disparaissent en grande partie :
 * il n'y a plus de barre de statut à couvrir. Le padding bas conserve
 * `env(safe-area-inset-bottom)` pour la barre gestuelle des mobiles.
 *
 * **Le "tap qui ne traverse pas"** (`onStartShouldSetResponder`) devient un
 * `stopPropagation` sur le clic — même intention, mécanique du DOM.
 *
 * **Responsive (§6)** : au-delà de `md`, la feuille cesse de monter du bas pour
 * devenir un panneau centré. Une feuille ancrée en bas est un idiome tactile ;
 * sur un grand écran elle serait loin du regard.
 *
 * Les deux animations distinctes (le fond se fond, la feuille monte) sont
 * conservées — c'est ce décalage qui donne l'impression « premium ». Elles
 * passent de Reanimated à des keyframes CSS (cf. index.css).
 */
export function AppModal({
  visible,
  onClose,
  title,
  subtitle,
  hideClose = false,
  dismissOnBackdrop = true,
  size = "auto",
  children,
}: AppModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const isTabletUp = useIsTabletUp();
  const hasHeader = Boolean(title) || !hideClose;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // `showModal()` (et non `show()`) est ce qui active le piégeage du focus et
    // l'inertisation du reste de la page.
    if (visible && !el.open) el.showModal();
    if (!visible && el.open) el.close();
  }, [visible]);

  if (!visible) return null;

  return (
    <dialog
      ref={ref}
      aria-label={title}
      // La touche Échap déclenche `cancel` : on la route vers onClose pour que
      // l'état React reste synchrone avec le DOM. Sur une feuille bloquante, on
      // annule l'événement — équivalent de `dismissOnBackdrop={false}`.
      onCancel={(e) => {
        if (!dismissOnBackdrop) {
          e.preventDefault();
          return;
        }
        onClose();
      }}
      // Le clic sur le ::backdrop remonte sur le <dialog> lui-même : c'est ce
      // qui permet de distinguer « fond » de « contenu », lequel arrête la
      // propagation ci-dessous.
      onClick={dismissOnBackdrop ? onClose : undefined}
      className={[
        "m-0 max-h-full max-w-full border-none bg-transparent p-0",
        "backdrop:bg-navy-900/55 backdrop:animate-none",
        isTabletUp
          ? "fixed inset-0 flex h-full w-full items-center justify-center"
          : "fixed inset-0 flex h-full w-full items-end justify-center",
      ].join(" ")}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={[
          "w-full bg-white",
          MAX_HEIGHT[size],
          "overflow-y-auto pt-3",
          isTabletUp
            ? "max-w-lg rounded-[28px] pb-4"
            : "rounded-t-[28px] pb-[max(16px,env(safe-area-inset-bottom,0px))]",
          isTabletUp ? "animate-[alomain-modal-in_220ms_ease-out]" : "animate-[alomain-sheet-in_220ms_ease-out]",
        ].join(" ")}
      >
        {/* Poignée de glissement : seulement en mobile, où le geste existe. */}
        {!isTabletUp && (
          <span aria-hidden className="mx-auto mb-3 block h-1 w-11 rounded-full bg-gray-200" />
        )}

        {hasHeader && (
          <>
            <div className="flex flex-row items-start justify-between gap-3 px-5 pb-3">
              <div className="min-w-0 flex-1">
                {title ? (
                  <AppText as="h2" variant="h3" color={colors.navy800} className="truncate block">
                    {title}
                  </AppText>
                ) : null}
                {subtitle ? (
                  <AppText as="p" variant="small" color={colors.gray500} className="mt-0.5 block">
                    {subtitle}
                  </AppText>
                ) : null}
              </div>

              {!hideClose && (
                <AppPressable
                  onClick={onClose}
                  aria-label="Fermer"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100"
                >
                  <Icon name="close" size={20} color={colors.navy800} />
                </AppPressable>
              )}
            </div>

            {/* Filet : `StyleSheet.hairlineWidth` (≈0.5px selon la densité)
                devient 1px CSS, que le navigateur affine lui-même en fonction
                du device pixel ratio. */}
            <div className="h-px bg-gray-200" />
          </>
        )}

        <div className="pt-3">{children}</div>
      </div>
    </dialog>
  );
}
